import { ApiError, isRecord, requireBoolean, requireNumber, requireString } from '@/lib/api'
import { apiErrorMessage } from '@/lib/api-error-message'

export type PolicyRefreshStatus = 'Unknown' | 'Pending' | 'Running' | 'Current' | 'Failed'

export function decodePolicyRefreshStatus(raw: unknown): PolicyRefreshStatus {
  switch (raw) {
    case 'Unknown':
    case 'Pending':
    case 'Running':
    case 'Current':
    case 'Failed':
      return raw
    default:
      throw new Error('Unexpected policy refresh status')
  }
}

export function decodePolicyMutation(raw: unknown) {
  if (!isRecord(raw)) throw new Error('Malformed policy receipt')
  return {
    id: requireString(raw.id, 'id'),
    revision: requireNumber(raw.revision, 'revision'),
    deleted: requireBoolean(raw.deleted, 'deleted'),
    refreshStatus: decodePolicyRefreshStatus(raw.refreshStatus),
  }
}

export function isDefinitePolicyCreateRejection(error: unknown, prefix: string) {
  if (!(error instanceof ApiError)) return false
  if (error.code === `${prefix}_IDEMPOTENCY_CONFLICT`) return false
  return [400, 403, 404, 409].includes(error.status)
    || error.code === `${prefix}_LOCK_TIMEOUT`
    || error.code === `${prefix}_EVALUATOR_UNAVAILABLE`
}

export function policyErrorMessage(
  error: unknown,
  prefix: string,
  valueLabel: string,
  operation: 'save' | 'load' | 'delete' = 'save',
) {
  const fallback = {
    save: `Couldn’t save the ${valueLabel} policy. Try again.`,
    load: `Couldn’t reload the ${valueLabel} policy. Your edits are kept. Try reloading again.`,
    delete: `Couldn’t delete the ${valueLabel} policy. Retry deletion, or cancel and reload the page to check whether it already succeeded.`,
  }[operation]
  const revisionConflict = operation === 'delete'
    ? 'This policy changed before deletion. Cancel, reload the page, and retry deletion using the latest revision.'
    : 'This policy was changed by someone else. Reload the latest policy before saving again. Reloading replaces your unsaved edits.'
  return apiErrorMessage(error, fallback, {
    codes: {
      [`${prefix}_ORDER_CONFLICT`]: 'The policy order changed. Reload the order, review the new position, and save again.',
      [`${prefix}_REVISION_CONFLICT`]: revisionConflict,
      [`${prefix}_INVALID_SELECTOR`]: 'The selector is invalid. Use target.id or target.name in a true/false expression.',
      [`${prefix}_AMBIGUOUS`]: 'Policies at this owner have conflicting precedence. Change their order or selectors before saving.',
      [`${prefix}_INVALID_VALUE`]: `The server rejected one or more ${valueLabel} values.`,
      [`${prefix}_EVALUATOR_UNAVAILABLE`]: 'Policy evaluation is unavailable. Try again shortly.',
      [`${prefix}_NOT_FOUND`]: 'This policy no longer exists. Cancel and reload the page to update the list.',
      [`${prefix}_IDEMPOTENCY_CONFLICT`]: 'This request already identifies a policy with different values. Cancel, reload the page, and edit the existing policy instead of submitting another create request.',
      [`${prefix}_LOCK_TIMEOUT`]: 'Policies are changing right now. Wait a moment and try again.',
      [`${prefix}_OVERRIDE_CONFLICT`]: 'Another policy depends on this policy. Remove that relationship before deleting it.',
    },
  })
}
